import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import api from 'services/api';
import SearchBar from 'components/search/SearchBar';
import Card from 'components/common/Card';
import ToggleView from 'components/archives/ToggleView';
import ArchivesGridView from 'components/archives/ArchivesGridView';
import ArchivesListView from 'components/archives/ArchivesListView';
import MapCta from 'components/common/MapCta';
import LiensPratiques from 'components/common/LiensPratiques';
import styles from './SearchResults.module.css';
import ListItem from 'components/archives/ListItem';
import { useSubSite } from 'contexts/SubSiteContext';
import useDebounce from 'hooks/useDebounce';
import { Helmet } from 'react-helmet-async';
import Spinner from 'components/common/Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt } from '@fortawesome/free-solid-svg-icons';

// Options de types pour react-select
const TYPE_OPTIONS = [
  { value: 'pages', label: 'Pages' },
  { value: 'events', label: 'Événements' },
  { value: 'kiosks', label: 'Kiosques' },
  { value: 'associations', label: 'Associations' },
  { value: 'news', label: 'Actualités' },
];

const SearchResults = () => {
  const [results, setResults] = useState({
    pages: [],
    events: [],
    kiosks: [],
    associations: [],
    news: [],
    webcams: [] // Nouvelle propriété pour les webcams
  });
  const [sortedResults, setSortedResults] = useState([]); // NOUVEAU: Résultats triés par pertinence
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [view, setView] = useState('grid');
  const [selectedTypes, setSelectedTypes] = useState(TYPE_OPTIONS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 12,
    total_results: 0,
    has_more: false
  });
  const RESULTS_PER_PAGE = 12;
  const location = useLocation();
  const navigate = useNavigate();
  const { isSubSite, subSiteSlug } = useSubSite();

  // Extraction des paramètres de l'URL
  const getQueryParams = (search) => {
    const params = new URLSearchParams(search);
    return {
      query: params.get('q') || '',
    };
  };

  const { query } = getQueryParams(location.search);
  const debouncedQuery = useDebounce(query, 500);

  // Réinitialiser la page quand la requête change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedQuery]);

  // Appel API pour récupérer les résultats
  useEffect(() => {
    if (debouncedQuery.trim().length === 0) {
      setResults({
        pages: [],
        events: [],
        kiosks: [],
        associations: [],
        news: [],
        webcams: []
      });
      setSortedResults([]); // NOUVEAU: Réinitialiser les résultats triés
      setPagination({
        current_page: 1,
        per_page: RESULTS_PER_PAGE,
        total_results: 0,
        has_more: false
      });
      setCurrentPage(1);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);

    const endpoint = '/search'; // Endpoint pour les recherches non-sous-site
    api
      .get(endpoint, { 
        params: { 
          q: debouncedQuery,
          page: currentPage,
          limit: RESULTS_PER_PAGE
        } 
      })
      .then((response) => {
        console.log('API Response:', response.data);
        if (response.data.status === 'success') {
          const data = response.data.data;
          const paginationData = response.data.pagination;
          const sortedData = response.data.sorted_results || []; // NOUVEAU: Récupérer les résultats triés
          
          setResults({
            pages: data.pages || [],
            events: data.events || [],
            kiosks: data.kiosks || [],
            associations: data.associations || [],
            news: data.news || [],
            webcams: data.webcams || []
          });
          
          setSortedResults(sortedData); // NOUVEAU: Stocker les résultats triés par pertinence
          
          if (paginationData) {
            setPagination(paginationData);
          }
          
          // Log pour debug de l'ordre de pertinence
          if (sortedData.length > 0) {
            console.log('🎯 Ordre de pertinence:', sortedData.map((item, index) => 
              `#${index + 1}: ${item.item.title} [${item.type}] (score: ${item.score})`
            ));
          }
        } else {
          setError('Erreur lors de la récupération des résultats.');
          setResults({
            pages: [],
            events: [],
            kiosks: [],
            associations: [],
            news: [],
            webcams: []
          });
          setSortedResults([]); // NOUVEAU: Réinitialiser en cas d'erreur
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Erreur lors de la recherche:', err);
        setError(err.response ? err.response.statusText : err.message);
        setResults({
          pages: [],
          events: [],
          kiosks: [],
          associations: [],
          news: [],
          webcams: []
        });
        setSortedResults([]); // NOUVEAU: Réinitialiser en cas d'erreur
        setLoading(false);
      });
  }, [debouncedQuery, currentPage]);

  // État pour autoriser l'annulation du filtre forcé
  const [forceKioskFilterDisabled, setForceKioskFilterDisabled] = useState(false);

  // Fonction de Levenshtein pour calculer la distance d'édition entre deux chaînes
  function levenshteinDistance(a, b) {
    const m = a.length,
      n = b.length;
    const dp = Array(m + 1)
      .fill(null)
      .map(() => Array(n + 1).fill(0));
    for (let i = 0; i <= m; i++) dp[i][0] = i;
    for (let j = 0; j <= n; j++) dp[0][j] = j;
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1,
          dp[i][j - 1] + 1,
          dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
        );
      }
    }
    return dp[m][n];
  }

  // Détection floue pour forcer le filtre "kiosques"
  const forceKioskFilter = useMemo(() => {
    const keywords = ['arras actu', 'actu', 'arras actu n°'];
    const checkArray = (arr) =>
      arr.some((item) => {
        if (!item.title) return false;
        const titleLower = item.title.toLowerCase();
        return keywords.some((keyword) => {
          if (titleLower.includes(keyword)) return true;
          const distance = levenshteinDistance(titleLower, keyword);
          return distance <= 2;
        });
      });
    return Object.values(results).some((arr) => checkArray(arr));
  }, [results]);

  // La force est effective si forceKioskFilter est vrai ET si l'utilisateur n'a pas désactivé ce comportement
  const effectiveForceKioskFilter = forceKioskFilter && !forceKioskFilterDisabled;

  // Si le filtrage forcé est actif, on force le Select à ne contenir que "Kiosques"
  const effectiveSelectedTypes = effectiveForceKioskFilter
    ? [{ value: 'kiosks', label: 'Kiosques' }]
    : selectedTypes;

  // Fonction pour transformer un item selon son type (utilisée pour les sorted_results)
  const transformItemByType = (item, type, index, score, rank) => {
    // Vérification de sécurité : s'assurer que l'item existe
    if (!item) {
      console.warn(`Item undefined à l'index ${index} pour le type ${type}`);
      return null;
    }

    switch (type) {
      case 'webcams':
        return {
          uniqueId: `webcams-${item.id || index}`,
          slug: item.slug,
          type: 'webcams',
          id: item.id || index,
          title: item.title,
          date: null,
          categories: [],
          thumbnail: `https://varras.mbscom.net//uploads/images/banniere-676d3358524e9985147523.jpg`,
          link: item.link,
          snippet: null,
          relevanceScore: score,
          relevanceRank: rank,
        };
      case 'pages':
        return {
          uniqueId: `pages-${item.id}`,
          slug: item.slug,
          type: 'pages',
          id: item.id,
          title: item.title,
          date: null,
          categories: item.category ? [item.category.name] : [],
          thumbnail: item.thumbnail
            ? `${process.env.REACT_APP_MEDIA_BASE_URL}/images/${item.thumbnail}`
            : null,
          link: `/page/${item.slug || item.id}`,
          snippet: item.snippet,
          category: item.category,
          relevanceScore: score,
          relevanceRank: rank,
        };
      case 'events':
        return {
          uniqueId: `events-${item.id}`,
          slug: item.slug,
          type: 'events',
          id: item.id,
          title: item.title,
          date:
            new Date(item.start).toLocaleDateString() +
            ' - ' +
            new Date(item.end).toLocaleDateString(),
          categories: item.category ? [item.category.name] : [],
          thumbnail: item.thumbnail
            ? `${process.env.REACT_APP_MEDIA_BASE_URL}/${item.thumbnail.path}`
            : null,
          link: `/evenement/${item.slug}`,
          snippet: item.snippet,
          relevanceScore: score,
          relevanceRank: rank,
        };
      case 'kiosks':
        return {
          uniqueId: `kiosks-${item.id}`,
          slug: item.slug,
          type: 'kiosks',
          id: item.id,
          title: item.title || 'Kiosk',
          date: null,
          categories: [],
          thumbnail: item.poster
            ? `${process.env.REACT_APP_MEDIA_BASE_URL}/${item.poster.path}`
            : null,
          link: item.serie?.slug && item.slug 
            ? `/kiosque/${item.serie.slug}/${item.slug}`
            : `/kiosque/${item.slug || item.id}`, // Fallback sécurisé
          description: item.description,
          relevanceScore: score,
          relevanceRank: rank,
        };
      case 'associations':
        return {
          uniqueId: `associations-${item.id}`,
          slug: item.slug,
          type: 'associations',
          id: item.id,
          title: item.name || 'Association',
          date: null,
          categories: [],
          thumbnail: item.logo
            ? `${process.env.REACT_APP_MEDIA_BASE_URL}/${item.logo}`
            : null,
          link: `/associations/${item.slug || item.id}`, // Fallback sécurisé
          description: item.description,
          relevanceScore: score,
          relevanceRank: rank,
        };
      case 'news':
        return {
          uniqueId: `news-${item.id}`,
          slug: item.slug,
          type: 'news',
          id: item.id,
          title: item.title,
          date: new Date(item.createdAt).toLocaleDateString(),
          categories: [],
          thumbnail: item.thumbnail
            ? `${process.env.REACT_APP_MEDIA_BASE_URL}/${item.thumbnail.path}`
            : null,
          link: `/actualite/${item.slug}`,
          content: item.content,
          relevanceScore: score,
          relevanceRank: rank,
        };
      default:
        return null;
    }
  };

  // Gestion du toggle entre vue "grid" et "list"
  const handleViewChange = (selectedView) => setView(selectedView);

  // Gestion des changements dans le Select
  const handleTypeChange = (selectedOptions) => {
    setSelectedTypes(selectedOptions.length > 0 ? selectedOptions : TYPE_OPTIONS);
  };

  // Préparation des items filtrés AVEC ORDRE DE PERTINENCE
  const filteredItems = useMemo(() => {
    const items = [];

    // NOUVEAU: Utiliser les résultats triés par pertinence au lieu de l'ordre groupé
    if (sortedResults && sortedResults.length > 0) {
      // Obtenir les types sélectionnés pour le filtrage
      const selectedTypeValues = effectiveSelectedTypes.map(type => type.value);
      
      sortedResults.forEach((resultWrapper, index) => {
        // Vérification de sécurité : s'assurer que resultWrapper existe
        if (!resultWrapper || typeof resultWrapper !== 'object') {
          console.warn(`ResultWrapper invalide à l'index ${index}:`, resultWrapper);
          return;
        }

        const { item: rawItem, type, score, rank } = resultWrapper;
        
        // Vérifications supplémentaires
        if (!type || !rawItem) {
          console.warn(`Données manquantes dans resultWrapper à l'index ${index}:`, resultWrapper);
          return;
        }
        
        // Filtrer par type sélectionné
        if (selectedTypeValues.includes(type)) {
          const transformedItem = transformItemByType(rawItem, type, index, score, rank);
          if (transformedItem) {
            items.push(transformedItem);
          }
        }
      });
    }

    // Fallback: Si pas de sorted_results, utiliser l'ancienne méthode (pour compatibilité)
    if (items.length === 0) {
      // Ajout prioritaire des webcams si présentes
      if (results.webcams && results.webcams.length > 0) {
        results.webcams.forEach((webcam, index) => {
          items.push({
            uniqueId: `webcams-${index}`,
            slug: webcam.slug,
            type: 'webcams',
            id: webcam.id || index,
            title: webcam.title,
            date: null,
            categories: [],
            thumbnail: `https://varras.mbscom.net//uploads/images/banniere-676d3358524e9985147523.jpg`,
            link: webcam.link,
            snippet: null,
          });
        });
      }

      // Traitement des autres types en fonction de effectiveSelectedTypes (ancienne méthode)
      const typesToFilter = effectiveSelectedTypes.length > 0 ? effectiveSelectedTypes : TYPE_OPTIONS;
      typesToFilter.forEach((type) => {
        const typeItems = (results[type.value] || []).map((item) => {
          switch (type.value) {
            case 'pages':
              return {
                uniqueId: `pages-${item.id}`,
                slug: item.slug,
                type: 'pages',
                id: item.id,
                title: item.title,
                date: null,
                categories: item.category ? [item.category.name] : [],
                thumbnail: item.thumbnail
                  ? `${process.env.REACT_APP_MEDIA_BASE_URL}/images/${item.thumbnail}`
                  : null,
                link: `/page/${item.slug || item.id}`,
                snippet: item.snippet,
                category: item.category,
              };
            case 'events':
              return {
                uniqueId: `events-${item.id}`,
                slug: item.slug,
                type: 'events',
                id: item.id,
                title: item.title,
                date:
                  new Date(item.start).toLocaleDateString() +
                  ' - ' +
                  new Date(item.end).toLocaleDateString(),
                categories: item.category ? [item.category.name] : [],
                thumbnail: item.thumbnail
                  ? `${process.env.REACT_APP_MEDIA_BASE_URL}/${item.thumbnail.path}`
                  : null,
                link: `/evenement/${item.slug}`,
                snippet: item.snippet,
              };
            case 'kiosks':
              return {
                uniqueId: `kiosks-${item.id}`,
                slug: item.slug,
                type: 'kiosks',
                id: item.id,
                title: item.title || 'Kiosk',
                date: null,
                categories: [],
                thumbnail: item.poster
                  ? `${process.env.REACT_APP_MEDIA_BASE_URL}/${item.poster.path}`
                  : null,
                link: `/kiosque/${item.serie.slug}/${item.slug}`,
                description: item.description,
              };
            case 'associations':
              return {
                uniqueId: `associations-${item.id}`,
                slug: item.slug,
                type: 'associations',
                id: item.id,
                title: item.name,
                date: null,
                categories: [],
                thumbnail: item.logo
                  ? `${process.env.REACT_APP_MEDIA_BASE_URL}/${item.logo}`
                  : null,
                link: `/associations/${item.slug}`,
                description: item.description,
              };
            case 'news':
              return {
                uniqueId: `news-${item.id}`,
                slug: item.slug,
                type: 'news',
                id: item.id,
                title: item.title,
                date: new Date(item.createdAt).toLocaleDateString(),
                categories: [],
                thumbnail: item.thumbnail
                  ? `${process.env.REACT_APP_MEDIA_BASE_URL}/${item.thumbnail.path}`
                  : null,
                link: `/actualite/${item.slug}`,
                content: item.content,
              };
            default:
              return null;
          }
        }).filter(Boolean);
        items.push(...typeItems);
      });
    }

    // Éliminer les doublons via uniqueId
    const uniqueItemsMap = new Map();
    items.forEach((item) => {
      if (!uniqueItemsMap.has(item.uniqueId)) {
        uniqueItemsMap.set(item.uniqueId, item);
      }
    });
    return Array.from(uniqueItemsMap.values());
  }, [results, sortedResults, effectiveSelectedTypes]);

  // Fonctions utilitaires pour obtenir lien, meta et description
  const getLinkByType = (item) => {
    if (!item || !item.type) {
      console.warn('Item invalide dans getLinkByType:', item);
      return '#';
    }

    switch (item.type) {
      case 'pages':
        return `/page/${item.slug || item.id || 'unknown'}`;
      case 'events':
        return `/evenement/${item.slug || item.id || 'unknown'}`;
      case 'kiosks':
        return item.link || '#';
      case 'associations':
        return `/associations/${item.slug || item.id || 'unknown'}`;
      case 'news':
        return `/actualite/${item.slug || item.id || 'unknown'}`;
      case 'webcams':
        return item.link || '#';
      default:
        return '#';
    }
  };

  const getMetaByType = (item) => {
    if (!item || !item.type) {
      return [];
    }

    switch (item.type) {
      case 'pages':
        return item.category ? [`Catégorie : ${item.category.name}`] : [];
      case 'events':
        return item.date ? [`Publié : ${item.date}`] : [];
      case 'news':
        return item.date ? [`Publié : ${item.date}`] : [];
      default:
        return [];
    }
  };

  const getDescriptionByType = (item) => {
    if (!item || !item.type) {
      return 'Pas de description disponible.';
    }

    switch (item.type) {
      case 'pages':
        return item.snippet || 'Pas de description disponible.';
      case 'events':
        return item.snippet || 'Pas de description disponible.';
      case 'kiosks':
        return item.description || 'Pas de description disponible.';
      case 'associations':
        return item.description || 'Pas de description disponible.';
      case 'news':
        return item.content && item.content.length > 200
          ? `${item.content.substring(0, 200)}...`
          : item.content || 'Pas de description disponible.';
      default:
        return 'Pas de description disponible.';
    }
  };

  const hasResults = filteredItems.length > 0;

  return (
    <>
      <Helmet>
        <title>Ville d'Arras | Résultats de recherche pour "{debouncedQuery}"</title>
        <meta name="description" content="Une description de la page pour le SEO." />
        <meta property="og:title" content="Ville d'Arras" />
        <meta property="og:description" content="Une description adaptée aux réseaux sociaux." />
        <link rel="canonical" href="https://arras.fr/" />
      </Helmet>
      <div className={styles.resultsPage}>
        {/* Barre de recherche */}
        <section className={styles.resultsHeader}>
          <div className={`global-container ${styles.headerContainer}`}>
            <h1 className={styles.resultsTitle}>
              Résultats de recherche pour "{debouncedQuery}"
            </h1>
            <SearchBar onSearch={(query) => navigate(`/recherches?q=${encodeURIComponent(query)}`)} />
            {/* Section de filtrage avec React Select */}
            <div className={styles.filterSection}>
              <p className={styles.filterLabel}>Filtrer par type :</p>
              {effectiveForceKioskFilter ? (
                <>
                  <Select
                    options={[{ value: 'kiosks', label: 'Kiosques' }]}
                    isMulti={false}
                    value={[{ value: 'kiosks', label: 'Kiosques' }]}
                    isDisabled
                    className={styles.reactSelect}
                    classNamePrefix="select"
                    placeholder="Kiosques"
                  />
                                      <span
                      className={styles.refreshIcon}
                      onClick={() => setForceKioskFilterDisabled(true)}
                      title="Annuler le filtrage forcé"
                    >
                      <FontAwesomeIcon icon={faSyncAlt} />
                    </span>
                  <p className={styles.notice}>
                    Le filtre est automatiquement défini sur "Kiosques" en raison de votre recherche.

                  </p>
                </>
              ) : (
                <Select
                  options={TYPE_OPTIONS}
                  isMulti
                  value={selectedTypes}
                  onChange={handleTypeChange}
                  className={styles.reactSelect}
                  classNamePrefix="select"
                  placeholder="Sélectionnez les types..."
                  closeMenuOnSelect={false}
                />
              )}
            </div>
          </div>
        </section>
        <div className="page-content">
          <section className={`global-section ${styles.resultsSection}`}>
            <div className="global-container">
              <ToggleView view={view} setView={handleViewChange} />
              {loading ? (
                <div className={styles.loading}><Spinner /></div>
              ) : (
                <>
                  {!hasResults && (
                    <div className={styles.noResults}>Aucun résultat</div>
                  )}
                  {hasResults && (
                    <>
                      {view === 'grid' && (
                        <ArchivesGridView
                          items={filteredItems
                            .filter(item => item && item.uniqueId && item.title) // Filtrer les éléments invalides
                            .map(item => ({
                              id: item.uniqueId,
                              title: item.title,
                              date: item.date,
                              categories: item.categories || [],
                              thumbnail: item.thumbnail,
                              link: getLinkByType(item),
                            }))}
                          renderItem={(item, index) => {
                            // Vérification de sécurité avant rendu
                            if (!item || !item.id || !item.title) {
                              console.warn('Item invalide dans renderItem:', item);
                              return null;
                            }
                            
                            return (
                              <Card
                                key={item.id}
                                thumbnail={item.thumbnail}
                                title={item.title}
                                date={item.date}
                                categories={item.categories || []}
                                link={item.link}
                                // DEBUG: Afficher le score de pertinence en développement
                                extraInfo={process.env.NODE_ENV === 'development' && filteredItems[index]?.relevanceScore 
                                  ? `Score: ${filteredItems[index].relevanceScore.toFixed(2)} | Rang: #${filteredItems[index].relevanceRank}` 
                                  : null}
                              />
                            );
                          }}
                        />
                      )}
                      {view === 'list' && (
                        <ArchivesListView
                          items={filteredItems
                            .filter(item => item && item.uniqueId && item.title) // Filtrer les éléments invalides
                            .map(item => ({
                              id: item.uniqueId,
                              title: item.title,
                              meta: getMetaByType(item),
                              description: getDescriptionByType(item),
                              link: getLinkByType(item),
                              categories: item.categories || [],
                            }))}
                          renderItem={(item, index) => {
                            // Vérification de sécurité avant rendu
                            if (!item || !item.id || !item.title) {
                              console.warn('Item invalide dans renderItem (list):', item);
                              return null;
                            }
                            
                            return (
                              <ListItem
                                key={item.id}
                                title={item.title}
                                meta={[
                                  ...(item.meta || []),
                                  // DEBUG: Ajouter le score aux métadonnées en développement
                                  ...(process.env.NODE_ENV === 'development' && filteredItems[index]?.relevanceScore 
                                    ? [`🎯 Score: ${filteredItems[index].relevanceScore.toFixed(2)} | Rang: #${filteredItems[index].relevanceRank}`]
                                    : [])
                                ]}
                                description={item.description}
                                categories={item.categories || []}
                                link={item.link}
                              />
                            );
                          }}
                        />
                      )}
                    </>
                  )}
                  
                  {/* Composant de pagination */}
                  {hasResults && pagination.total_results > RESULTS_PER_PAGE && (
                    <div className={styles.paginationContainer}>
                      <div className={styles.paginationInfo}>
                        <span>
                          Page {pagination.current_page} - {pagination.total_results} résultat{pagination.total_results > 1 ? 's' : ''} trouvé{pagination.total_results > 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className={styles.paginationControls}>
                        <button 
                          className={styles.paginationButton}
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1 || loading}
                        >
                          Précédent
                        </button>
                        
                        <span className={styles.currentPage}>
                          {currentPage}
                        </span>
                        
                        <button 
                          className={styles.paginationButton}
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={!pagination.has_more || loading}
                        >
                          Suivant
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
          <MapCta />
          <LiensPratiques />
        </div>
      </div>
    </>
  );
};

export default SearchResults;
